<template>
  <div class="animated fadeIn">
    <!-- NEW: Sleek, non-blocking alert component -->
    <b-alert
      :show="alert.dismissCountDown"
      dismissible
      :variant="alert.variant"
      @dismissed="alert.dismissCountDown = 0"
      @dismiss-count-down="countDownChanged"
      class="position-fixed shadow-sm"
      style="
        top: 20px;
        right: 50%;
        transform: translateX(50%);
        z-index: 2000;
        max-width: 420px;
        width: 90%;
        padding: 12px 20px;
        font-size: 16px;
        border-radius: 8px;
        line-height: 1.5;
        font-weight: 500;
      "
    >
      <i
        v-if="alert.variant === 'success'"
        class="fa fa-check-circle mr-2 text-white"
      ></i>
      <i
        v-else-if="alert.variant === 'danger'"
        class="fa fa-exclamation-triangle mr-2 text-white"
      ></i>
      {{ alert.message }}
    </b-alert>

    <!-- SMS scheduler table -->
    <div class="card">
      <!-- Card Header with Inline Button -->
      <div class="card-header d-flex align-items-center py-2" style="gap: 1rem;">
        <h5 class="mb-0">Scheduler List</h5>
        <button
          type="button"
          @click.prevent="addScheduler()"
          style="
            background-color: #00a7e1;
            color: white;
            border: none;
            outline: none;
            padding: 4px 10px;
            font-size: 14px;
            font-weight: 400;
            display: inline-flex;
            align-items: center;
            cursor: pointer;
          "
        >
          <i class="fa fa-plus" style="margin-right: 5px;"></i>
          Add Scheduler
        </button>
      </div>

      <!-- Card Body with Table -->
      <div class="card-body">
        <vue-good-table
          mode="remote"
          ref="my-table"
          :totalRows="totalRecords"
          :columns="columns4"
          :rows="rows4"
          :pagination-options="{
            enabled: true,
            perPage: 6,
            perPageDropdown: [6, 10, 20],
            dropdownAllowAll: false
          }"
          styleClass="vgt-table striped bordered"
          @on-page-change="onPageChange"
          @on-per-page-change="onPerPageChange"
        >
          <template slot="table-row" slot-scope="props">
            <!-- MODIFIED: Edit and Delete Icons -->
            <span v-if="props.column.field == 'action'">
              <i
                class="fa fa-pencil"
                style="color: #63c2de; font-size: 20px; cursor: pointer; margin-right: 10px;"
                title="Edit Scheduler"
                v-on:click="editScheme(props.row)"
              ></i>
              <i
                class="fa fa-trash"
                style="color: #dc3545; font-size: 20px; cursor: pointer;"
                title="Delete Scheduler"
                v-on:click="showDeleteConfirmModal(props.row)"
              ></i>
            </span>

            <!-- MODIFIED: Status Toggle Switch -->
            <span v-else-if="props.column.field == 'is_active'">
              <label class="custom-toggle">
                <input
                  type="checkbox"
                  :checked="props.row.is_active"
                  @change="toggleStatus(props.row)"
                />
                <span class="slider"></span>
              </label>
            </span>

            <!-- NEW: Human-readable cron expression -->
            <span v-else-if="props.column.field == 'cron_expression'">
              {{ describeCron(props.row.cron_expression) }}
            </span>

            <!-- NEW: Truncated message with tooltip -->
            <span v-else-if="props.column.field == 'sms_template'">
              <span 
                class="d-inline-block text-truncate" 
                style="max-width: 280px;" 
                v-b-tooltip.hover 
                :title="props.row.sms_template"
              >
                {{ props.row.sms_template }}
              </span>
            </span>

            <!-- Fallback for other cells -->
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>

    <!-- Edit/Add Modal -->
<b-modal
  :title="tit"
  no-close-on-esc
  no-close-on-backdrop
  size="lg"
  class="modal-info"
  hide-footer
  v-model="largeModal"
  @hidden="reset"
>
  <!-- CRON BUILDER UI -->
  <b-card header="Schedule Builder" class="mb-4">
    <b-row>
      <b-col sm="4">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Schedule Type</template>
          <b-form-select v-model="cronBuilder.mode" :options="cronBuilder.modeOptions"></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Minutes -->
    <b-row v-if="cronBuilder.mode === 'minutes'">
      <b-col sm="6">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Run every:</template>
          <b-input-group append="minutes">
            <b-form-input type="number" v-model.number="cronBuilder.values.minuteInterval" min="1" max="59"></b-form-input>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Hourly -->
    <b-row v-if="cronBuilder.mode === 'hourly'">
      <b-col sm="6">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Run at minute:</template>
          <b-form-select v-model="cronBuilder.values.minute" :options="minuteOptions"></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Daily -->
    <b-row v-if="cronBuilder.mode === 'daily'">
      <b-col sm="4">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Hour (24h)</template>
          <b-form-select v-model="cronBuilder.values.hour" :options="hourOptions"></b-form-select>
        </b-form-group>
      </b-col>
      <b-col sm="4">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Minute</template>
          <b-form-select v-model="cronBuilder.values.minute" :options="minuteOptions"></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Weekly -->
    <b-row v-if="cronBuilder.mode === 'weekly'">
      <b-col sm="12">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Run on days</template>
          <b-form-checkbox-group v-model="cronBuilder.values.daysOfWeek" :options="dayOfWeekOptions" name="flavour-2a" class="pt-2"></b-form-checkbox-group>
        </b-form-group>
      </b-col>
      <b-col sm="4">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Hour (24h)</template>
          <b-form-select v-model="cronBuilder.values.hour" :options="hourOptions"></b-form-select>
        </b-form-group>
      </b-col>
      <b-col sm="4">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Minute</template>
          <b-form-select v-model="cronBuilder.values.minute" :options="minuteOptions"></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Monthly -->
    <b-row v-if="cronBuilder.mode === 'monthly'">
      <!-- MODIFIED: Day of Month with Day Picker -->
      <b-col sm="4">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Day of Month</template>
          <!-- Wrapper for positioning the picker -->
          <div class="position-relative" ref="dayPickerWrapper">
              <b-form-input
                :value="cronBuilder.values.dayOfMonth"
                @click.native="toggleDayPicker"
                placeholder="Select a day"
                readonly
                class="cursor-pointer"
              ></b-form-input>

              <!-- The Day Picker Grid -->
              <div v-if="showDayPicker" class="day-picker-container shadow-sm border rounded">
                <div class="day-picker-grid">
                  <div
                    v-for="day in 31"
                    :key="day"
                    class="day-cell"
                    :class="{ 'selected': day === cronBuilder.values.dayOfMonth }"
                    @click="selectDay(day)"
                  >
                    {{ day }}
                  </div>
                </div>
              </div>
          </div>
        </b-form-group>
      </b-col>
      <b-col sm="4">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Hour (24h)</template>
          <b-form-select v-model="cronBuilder.values.hour" :options="hourOptions"></b-form-select>
        </b-form-group>
      </b-col>
      <b-col sm="4">
        <b-form-group>
          <template #label><span class="text-danger font-weight-bold">*</span> Minute</template>
          <b-form-select v-model="cronBuilder.values.minute" :options="minuteOptions"></b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Hidden Cron Expression -->
    <b-form-input v-model="cardData.cron_expression" class="d-none"></b-form-input>
    <small class="form-text text-muted">
      <strong style="color: red;">{{ hasErrors.cron_expression }}</strong>
    </small>
  </b-card>

  <!-- REST OF THE FORM -->
  <b-row>
    <b-col sm="6">
      <b-form-group>
        <template #label><span class="text-danger font-weight-bold">*</span> Cron Job Name</template>
<b-form-select
  v-model="cardData.cron_name"
  :options="[{ value: '', text: 'Select Cron Job Name' }, ...cronOptions]"
  :disabled="editBtn"
  class="no-disabled-style"
>
</b-form-select>
        <small class="form-text text-muted">
          <strong style="color: red;">{{ hasErrors.cron_name }}</strong>
        </small>
      </b-form-group>
    </b-col>
    <b-col sm="6">
      <b-form-group>
        <template #label><span class="text-danger font-weight-bold">*</span> SMS Sender</template>
        <b-form-input
          type="text"
          placeholder="Enter Sender Name"
          autocomplete="off"
          v-model="cardData.sms_sender"
        ></b-form-input>
        <small class="form-text text-muted">
          <strong style="color: red;">{{ hasErrors.sms_sender }}</strong>
        </small>
      </b-form-group>
    </b-col>
  </b-row>
  <b-row>
    <b-col sm="4">
      <b-form-group>
        <template #label><span class="text-danger font-weight-bold">*</span> Template ID</template>
        <b-form-input
          type="text"
          placeholder="Enter Template ID"
          autocomplete="off"
          v-model="cardData.sms_template_id"
        ></b-form-input>
        <small class="form-text text-muted">
          <strong style="color: red;">{{ hasErrors.sms_template_id }}</strong>
        </small>
      </b-form-group>
    </b-col>
    <b-col sm="4">
      <b-form-group>
        <template #label><span class="text-danger font-weight-bold">*</span> Sender Mobile No.</template>
        <b-form-input
          type="text"
          placeholder="Enter Sender Mobile number"
          autocomplete="off"
          v-model="cardData.sender_mob"
        ></b-form-input>
        <small class="form-text text-muted">
          <strong style="color: red;">{{ hasErrors.sender_mob }}</strong>
        </small>
      </b-form-group>
    </b-col>
    <b-col sm="4">
      <b-form-group>
        <template #label><span class="text-danger font-weight-bold">*</span> API Key</template>
        <b-form-input
          type="text"
          placeholder="Enter API Key"
          autocomplete="off"
          v-model="cardData.api_key"
        ></b-form-input>
        <small class="form-text text-muted">
          <strong style="color: red;">{{ hasErrors.api_key }}</strong>
        </small>
      </b-form-group>
    </b-col>
  </b-row>
  <b-row>
    <b-col sm="6">
      <b-form-group>
        <template #label><span class="text-danger font-weight-bold">*</span> Entity ID</template>
        <b-form-input
          type="text"
          placeholder="Enter Entity ID"
          autocomplete="off"
          v-model="cardData.entity_id"
        ></b-form-input>
        <small class="form-text text-muted">
          <strong style="color: red;">{{ hasErrors.entity_id }}</strong>
        </small>
      </b-form-group>
    </b-col>
    <b-col sm="6">
      <b-form-group>
        <template #label><span class="text-danger font-weight-bold">*</span> Username</template>
        <b-form-input
          type="text"
          placeholder="Enter Username"
          autocomplete="off"
          v-model="cardData.username"
        ></b-form-input>
        <small class="form-text text-muted">
          <strong style="color: red;">{{ hasErrors.username }}</strong>
        </small>
      </b-form-group>
    </b-col>
  </b-row>
  <b-row>
    <b-col sm="12">
      <b-form-group>
        <template #label><span class="text-danger font-weight-bold">*</span> Template Message</template>
        <b-form-textarea
          placeholder="Enter Template Message"
          autocomplete="off"
          v-model="cardData.sms_template"
          rows="4"
        ></b-form-textarea>
        <small class="form-text text-muted">
          <strong style="color: red;">{{ hasErrors.sms_template }}</strong>
        </small>
      </b-form-group>
    </b-col>
  </b-row>

  <div class="card-footer modal_footer">
    <b-row>
      <b-col sm="12">
        <b-button-group>
          <b-button
            class="d-sm-down-none btn-sm"
            v-if="saveBtn"
            @click.prevent="cardScheme()"
            variant="primary"
          >
            <i class="fa fa-dot-circle-o"></i> Save
          </b-button>
          <b-button
            class="d-sm-down-none btn-sm"
            v-if="editBtn"
            @click.prevent="editSchemedata()"
            variant="primary"
          >
            <i class="fa fa-dot-circle-o"></i> Edit
          </b-button>
        </b-button-group>
      </b-col>
    </b-row>
  </div>
</b-modal>


    <!-- Delete Confirmation Modal -->
    <b-modal
      id="delete-confirm-modal"
      title="Confirm Deletion"
      ok-variant="danger"
      ok-title="Delete"
      cancel-title="Cancel"
      v-model="showDeleteModal"
      @ok="deleteCronEntry(deleteCronId)"
      @hidden="resetDeleteModal"
    >
      <p>Are you sure you want to delete this cron job?</p>
    </b-modal>
  </div>
</template>

<style scoped>
.modal-title {
  color: black;
  font-weight: bold;
}
.vgt-table td {
  padding: 0.75rem !important;
  vertical-align: middle !important;
}

.custom-toggle {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}

.custom-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.custom-toggle .slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #ccc;
  border-radius: 22px;
  transition: 0.3s;
}

.custom-toggle .slider::before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}

.custom-toggle input:checked + .slider {
  background-color: #28a745; /* green */
}

.custom-toggle input:checked + .slider::before {
  transform: translateX(18px);
}
/* NEW: Styles for the Day Picker */
.cursor-pointer {
  cursor: pointer;
  background-color: #fff !important;
}
.day-picker-container {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1051; /* Higher than modal z-index */
  background-color: white;
  margin-top: 4px;
  width: 245px; /* Fits 7 columns nicely */
  padding: 8px;
}
.day-picker-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.day-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  width: 28px;
  cursor: pointer;
  border-radius: 50%;
  font-size: 14px;
  transition: background-color 0.2s, color 0.2s;
  margin: auto;
}
.day-cell:hover {
  background-color: #e9ecef;
}
.day-cell.selected {
  background-color: #00a7e1;
  color: white;
  font-weight: bold;
}
</style>

<script>
export default {
  data() {
    return {
      super_user: 3,
      largeModal: false,
      loading: false,
      msg_id: '',
      totalRecords: 0,
      serverParams: {
        page: 1,
        perPage: 6
      },
      cardData: {
        cron_name: '',
        cron_expression: '',
        sms_sender: '',
        sms_template_id: '',
        sms_template: '',
        sender_mob: '',
        api_key: '',
        entity_id: '',
        username: '',
        is_active: true,
      },
      hasErrors: {
        cron_name: '',
        cron_expression: '',
        sms_sender: '',
        sms_template_id: '',
        sms_template: '',
        sender_mob: '',
        api_key: '',
        entity_id: '',
        username: '',
      },
      alert: {
        dismissCountDown: 0,
        variant: '',
        message: ''
      },
      cronOptions: [],
      tit: '',
      saveBtn: false,
      editBtn: false,
      rows4: [],
      columns4: [
        { label: "Cron Job Name", field: "cron_name", width: "15% !important", thClass: "text-center", tdClass: "text-center" },
        { label: "Schedule", field: "cron_expression", width: "15% !important", thClass: "text-center", tdClass: "text-center" },
        { label: "SMS Sender", field: "sms_sender", width: "10% !important", thClass: "text-center", tdClass: "text-center" },
        { label: "Template ID", field: "sms_template_id", width: "10% !important", thClass: "text-center", tdClass: "text-center" },
        { label: "Message", field: "sms_template", width: "23% !important", thClass: "text-center" },
        { label: "Username", field: "username", width: "10% !important", thClass: "text-center", tdClass: "text-center" },
        { label: "Status", field: "is_active", width: "10% !important", tdClass: "text-center", thClass: "text-center" },
        { label: "Action", field: "action", width: "12% !important", thClass: "text-center", tdClass: "text-center" },
      ],
      debounceTimer: null,
      cronBuilder: {
        mode: 'minutes',
        modeOptions: [
          { value: 'minutes', text: 'Minutes' },
          { value: 'hourly', text: 'Hourly' },
          { value: 'daily', text: 'Daily' },
          { value: 'weekly', text: 'Weekly' },
          { value: 'monthly', text: 'Monthly' },
        ],
        values: {
          minuteInterval: 5,
          minute: '0',
          hour: '9',
          daysOfWeek: [],
          dayOfMonth: 1,
        }
      },
      minuteOptions: [],
      hourOptions: [],
      dayOfWeekOptions: [
        { text: 'Sunday', value: 0 }, { text: 'Monday', value: 1 }, { text: 'Tuesday', value: 2 },
        { text: 'Wednesday', value: 3 }, { text: 'Thursday', value: 4 }, { text: 'Friday', value: 5 }, { text: 'Saturday', value: 6 },
      ],
      showDeleteModal: false,
      deleteCronId: null,
      showDayPicker: false, // NEW: State for day picker visibility
    };
  },
  watch: {
    'cronBuilder.values': {
      handler: 'generateCronString',
      deep: true
    },
    'cronBuilder.mode': 'generateCronString'
  },
  mounted() {
    // NEW: Add listener for clicks outside the day picker to close it
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    // NEW: Clean up the listener when the component is destroyed
    document.removeEventListener('click', this.handleClickOutside);
  },
  created() {
    this.super_user = this.$store.getters.get_super_user;
    this.getCronList();
    this.goldScheme();
    for (let i = 0; i < 60; i++) {
      this.minuteOptions.push({ value: String(i), text: String(i).padStart(2, '0') });
      if (i < 24) {
        this.hourOptions.push({ value: String(i), text: String(i).padStart(2, '0') });
      }
    }
  },
  methods: {
    // NEW: Day Picker Methods
    handleClickOutside(event) {
      if (this.showDayPicker && this.$refs.dayPickerWrapper && !this.$refs.dayPickerWrapper.contains(event.target)) {
        this.showDayPicker = false;
      }
    },
    toggleDayPicker() {
      this.showDayPicker = !this.showDayPicker;
    },
    selectDay(day) {
      this.cronBuilder.values.dayOfMonth = day;
      this.showDayPicker = false;
    },
    // ---
    describeCron(cron) {
      if (!cron) return 'N/A';
      const [min, hour, day, month, weekday] = cron.trim().split(' ');
      const describePart = (value, unit) => {
        if (value === '*') return `every ${unit}`;
        if (value.startsWith('*/')) return `every ${value.slice(2)} ${unit}s`;
        if (value.includes(',')) return `${unit}s at ${value.split(',').join(', ')}`;
        if (value.includes('-')) {
          const [start, end] = value.split('-');
          return `${unit}s from ${start} to ${end}`;
        }
        return `${unit} ${value}`;
      };
      const describeTime = () => {
        const minuteDesc = describePart(min, 'minute');
        const hourDesc = describePart(hour, 'hour');
        if (hour === '*' && min === '*') return 'Every minute';
        if (hour === '*') return `${minuteDesc}, every hour`;
        if (min === '*') return `${hourDesc}, every minute`;
        return `At ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
      };
      const describeDay = () => {
        let parts = [];
        if (day !== '*') parts.push(`on day ${day}`);
        if (month !== '*') parts.push(`in ${this.getMonthName(month)}`);
        if (weekday !== '*' && weekday !== '?') parts.push(`on ${this.getWeekdayName(weekday)}`);
        if (day === '*' && month === '*' && (weekday === '*' || weekday === '?')) parts.push('every day');
        return parts.join(', ');
      };
      try {
        const timePart = describeTime();
        const dayPart = describeDay();
        return `${timePart}, ${dayPart}`.replace(/^./, str => str.toUpperCase());
      } catch (e) {
        return cron;
      }
    },
    getWeekdayName(numStr) {
       const days = {0:'Sunday',1:'Monday',2:'Tuesday',3:'Wednesday',4:'Thursday',5:'Friday',6:'Saturday'};
       const selectedDays = numStr.split(',').map(n => days[parseInt(n,10)] || `Day ${n}`).join(', ');
       return selectedDays;
    },
    getMonthName(num) {
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return months[parseInt(num) - 1] || `Month ${num}`;
    },
    toggleStatus(row) {
      this.loading = true;
      const updatedRow = { ...row, is_active: !row.is_active };
      this.$http
        .post('api/cronjobdata/updateCronEntry', updatedRow)
        .then(response => {
          if (response.status === 200) {
            this.showAlert(`Status updated to ${updatedRow.is_active ? 'Active' : 'Inactive'}`, "success");
            const index = this.rows4.findIndex(r => r.id === row.id);
            if (index !== -1) {
              this.$set(this.rows4, index, updatedRow);
            }
          }
          this.loading = false;
        })
        .catch(error => {
          this.loading = false;
          this.showAlert("Error updating status.", "danger");
          console.error('Error updating status:', error);
        });
    },
    showAlert(message, variant = 'success', duration = 5) {
      this.alert.message = message;
      this.alert.variant = variant;
      this.alert.dismissCountDown = duration;
    },
    countDownChanged(dismissCountDown) {
      this.alert.dismissCountDown = dismissCountDown;
    },
    generateCronString() {
      const values = this.cronBuilder.values;
      let cron = ['*', '*', '*', '*', '*'];
      switch (this.cronBuilder.mode) {
        case 'minutes':
          cron[0] = `*/${values.minuteInterval || 5}`;
          break;
        case 'hourly':
          cron[0] = values.minute;
          cron[1] = `*`; // run every hour at the specified minute
          break;
        case 'daily':
          cron[0] = values.minute;
          cron[1] = values.hour;
          break;
        case 'weekly':
          cron[0] = values.minute;
          cron[1] = values.hour;
          cron[4] = values.daysOfWeek.length > 0 ? values.daysOfWeek.sort().join(',') : '*';
          break;
        case 'monthly':
          cron[0] = values.minute;
          cron[1] = values.hour;
          cron[2] = values.dayOfMonth || 1;
          break;
      }
      this.cardData.cron_expression = cron.join(' ');
    },
    parseCronExpression(cronString) {
      if (!cronString) {
        this.cronBuilder.mode = 'minutes';
        this.cronBuilder.values = { minuteInterval: 5, minute: '0', hour: '9', daysOfWeek: [], dayOfMonth: 1 };
        return;
      }
      const parts = cronString.split(' ');
      const [minute, hour, dayOfMonth, , dayOfWeek] = parts;
      
      this.cronBuilder.values = { minuteInterval: 5, minute: '0', hour: '9', daysOfWeek: [], dayOfMonth: 1 };

      if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*') {
          this.cronBuilder.mode = 'minutes';
          this.cronBuilder.values.minuteInterval = parseInt(minute.substring(2), 10);
      } else if (minute !== '*' && hour === '*' && dayOfMonth === '*') {
          this.cronBuilder.mode = 'hourly';
          this.cronBuilder.values.minute = minute;
      } else if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && (dayOfWeek === '*' || dayOfWeek === '?')) {
          this.cronBuilder.mode = 'daily';
          this.cronBuilder.values.minute = minute;
          this.cronBuilder.values.hour = hour;
      } else if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && dayOfWeek !== '*' && dayOfWeek !== '?') {
          this.cronBuilder.mode = 'weekly';
          this.cronBuilder.values.minute = minute;
          this.cronBuilder.values.hour = hour;
          this.cronBuilder.values.daysOfWeek = dayOfWeek.split(',').map(Number);
      } else if (minute !== '*' && hour !== '*' && dayOfMonth !== '*' && (dayOfWeek === '*' || dayOfWeek === '?')) {
          this.cronBuilder.mode = 'monthly';
          this.cronBuilder.values.minute = minute;
          this.cronBuilder.values.hour = hour;
          this.cronBuilder.values.dayOfMonth = parseInt(dayOfMonth, 10);
      } else {
          // Default fallback
          this.cronBuilder.mode = 'minutes';
      }
    },
    getCronList() {
      var _this = this;
      this.$http
        .get('api/cronjobs/getCronLists')
        .then(function (response) {
          _this.cronOptions = response.data.data.map(item => ({
            value: item.cron_name,
            text: item.cron_name
          }));
        })
        .catch(function (error) {
          console.error('Error fetching cron list:', error);
        });
    },
    goldScheme() {
      var _this = this;
      this.loading = true;
      const params = JSON.stringify(this.serverParams);
      this.$http
        .get(`api/cronjobdata/getAllCrons?params=${encodeURIComponent(params)}`)
        .then(function (response) {
          if (response.data.success) {
            _this.rows4 = response.data.data;
            _this.totalRecords = response.data.pagination.total;
            _this.serverParams.page = response.data.pagination.currentPage;
            _this.serverParams.perPage = response.data.pagination.perPage;
          } else {
            _this.showAlert("Error fetching cron jobs.", "danger");
          }
          _this.loading = false;
        })
        .catch(function (error) {
          _this.loading = false;
          _this.showAlert("Error fetching cron jobs.", "danger");
          console.error('Error fetching cron jobs:', error);
        });
    },
    onPageChange(params) {
      this.serverParams.page = params.currentPage;
      this.goldScheme();
    },
    onPerPageChange(params) {
      this.serverParams.perPage = params.currentPerPage;
      this.serverParams.page = 1; // Reset to first page when perPage changes
      this.goldScheme();
    },
    addScheduler() {
      this.reset();
      this.saveBtn = true;
      this.editBtn = false;
      this.tit = "Add Scheduler";
      this.generateCronString();
      this.largeModal = true;
    },
    validateForm() {
      let isValid = true;
      this.hasErrors = {
        cron_name: '',
        cron_expression: '',
        sms_sender: '',
        sms_template_id: '',
        sms_template: '',
        sender_mob: '',
        api_key: '',
        entity_id: '',
        username: '',
      };

      if (!this.cardData.cron_name) {
        this.hasErrors.cron_name = "Please select a Cron Job Name.";
        isValid = false;
      }
      if (!this.cardData.cron_expression || this.cardData.cron_expression.trim() === '* * * * *') {
        this.hasErrors.cron_expression = "Please configure a specific schedule using the builder.";
        isValid = false;
      }
      if (this.cronBuilder.mode === 'weekly' && this.cronBuilder.values.daysOfWeek.length === 0) {
        this.hasErrors.cron_expression = "Please select at least one day for the weekly schedule.";
        isValid = false;
      }
      if (!this.cardData.sms_sender) {
        this.hasErrors.sms_sender = "Please enter the SMS Sender name.";
        isValid = false;
      }
      if (!this.cardData.sms_template_id) {
        this.hasErrors.sms_template_id = "Please enter the Template ID.";
        isValid = false;
      }
      if (!this.cardData.sms_template) {
        this.hasErrors.sms_template = "Please enter the Template Message.";
        isValid = false;
      }
      if (!this.cardData.sender_mob) {
        this.hasErrors.sender_mob = "Please enter the Sender Mobile Number.";
        isValid = false;
      } else if (!/^\d{10}$/.test(String(this.cardData.sender_mob).trim())) {
        this.hasErrors.sender_mob = "Please enter a valid 10-digit mobile number.";
        isValid = false;
      }
      if (!this.cardData.api_key) {
        this.hasErrors.api_key = "Please enter the API Key.";
        isValid = false;
      }
      if (!this.cardData.entity_id) {
        this.hasErrors.entity_id = "Please enter the Entity ID.";
        isValid = false;
      }
      if (!this.cardData.username) {
        this.hasErrors.username = "Please enter the Username.";
        isValid = false;
      }

      return isValid;
    },
    cardScheme() {
      this.loading = true;
      if (this.validateForm()) {
        this.submitCronEntry();
      } else {
        this.loading = false;
      }
    },
    submitCronEntry() {
      var _this = this;
      this.$http
        .post('api/cronjobdata/addCronEntry', this.cardData)
        .then(function (response) {
          if (response.status == 200) {
            _this.loading = false;
            _this.largeModal = false;
            _this.goldScheme();
            _this.showAlert("Cron Job Added Successfully", "success");
          }
        })
        .catch(function (error) {
          _this.loading = false;
          _this.showAlert("Error adding cron job.", "danger");
          console.error('Error adding cron job:', error);
        });
    },
    editScheme(scheme) {
      this.reset();
      this.saveBtn = false;
      this.editBtn = true;
      this.tit = "Edit Scheduler";
      this.cardData = { ...scheme };
      this.msg_id = scheme.id;
      this.parseCronExpression(scheme.cron_expression);
      this.largeModal = true;
    },
    editSchemedata() {
      this.loading = true;
      if (this.validateForm()) {
        this.submitUpdateCronEntry();
      } else {
        this.loading = false;
      }
    },
    submitUpdateCronEntry() {
      var _this = this;
      let res = {
        id: this.msg_id,
        ...this.cardData
      };
      this.$http
        .post('api/cronjobdata/updateCronEntry', res)
        .then(function (response) {
          if (response.status == 200) {
            _this.loading = false;
            _this.largeModal = false;
            _this.goldScheme();
            _this.showAlert("Cron Job Updated Successfully", "success");
          }
        })
        .catch(function (error) {
          _this.loading = false;
          _this.showAlert("Error updating cron job.", "danger");
          console.error('Error updating cron job:', error);
        });
    },
    showDeleteConfirmModal(row) {
      this.deleteCronId = row.id;
      this.showDeleteModal = true;
    },
    resetDeleteModal() {
      this.deleteCronId = null;
      this.showDeleteModal = false;
    },
    deleteCronEntry(id) {
      var _this = this;
      this.loading = true;
      this.$http
        .post('api/cronjobdata/removeCronEntry', { id })
        .then(function (response) {
          if (response.status === 200) {
            _this.loading = false;
            _this.largeModal = false;
            _this.showDeleteModal = false;
            _this.goldScheme();
            _this.showAlert("Cron Job Deleted Successfully", "success");
          }
        })
        .catch(function (error) {
          _this.loading = false;
          _this.showDeleteModal = false;
          _this.showAlert("Error deleting cron job.", "danger");
          console.error('Error deleting cron job:', error);
        });
    },
    reset() {
      this.cardData = {
        cron_name: '',
        cron_expression: '',
        sms_sender: '',
        sms_template_id: '',
        sms_template: '',
        sender_mob: '',
        api_key: '',
        entity_id: '',
        username: '',
        is_active: true,
      };
      this.hasErrors = {
        cron_name: '',
        cron_expression: '',
        sms_sender: '',
        sms_template_id: '',
        sms_template: '',
        sender_mob: '',
        api_key: '',
        entity_id: '',
        username: '',
      };
      this.cronBuilder.mode = 'minutes';
      this.cronBuilder.values = { minuteInterval: 5, minute: '0', hour: '9', daysOfWeek: [], dayOfMonth: 1 };
      this.msg_id = '';
      this.saveBtn = false;
      this.editBtn = false;
      this.showDayPicker = false; // Reset picker state
    },
  },
};
</script>


// WEBPACK FOOTER //
// src/views/gold/schedulesms.vue